import {
  ANALYTICS_EVENTS,
  COMMON_PLOT_CONFIG,
  HIPHEN_GREEN,
  NULL_GROUP_LABEL,
  THEME,
} from "../../../../constants";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useCallback, useMemo, useState } from "react";

import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import { PropertySelect } from "../../../../components/PropertySelect/PropertySelect";
import ReactSlider from "react-slider";
import { slideExportConfig } from "../../../components/charts/slideExport";
import { useAttributeColor } from "../../../../hooks/useAttributeColor";
import { useGetGroupColor } from "../../../../hooks/useGetGroupColor";
import { useGetPropertyCategory } from "../../../../hooks/useGetPropertyCategory";
import { useSelector } from "react-redux";
import { useTracking } from "../../../../analytics";

// How many cards the Plots tab holds before the page suffers: each one mounts
// its own image history. The threshold is on the RESULTING TOTAL, not on the
// rectangle being drawn, because several modest selections add up just as fast
// as one big one.
const PLOTS_TAB_LIMIT = 150;

// Takes the height left over instead of claiming all of it: the card is a flex
// column holding the plot AND the colouring controls below it, and `height: 100%`
// pushed those controls out of a card whose `overflow: hidden` then clipped them
// (visible once expanded, where the card has a fixed height).
const PLOT_STYLE = { width: "100%", flex: "1 1 0", minHeight: 0 };

/* Shared by the clicked-plot overlay marker and its annotation arrow so the
 * highlighted point and the arrow pointing to it always match in color. */
const SELECTED_PLOT_COLOR = "#9aa0a6";

// Also the title of its card on the PCA page.
export const PCA_SCATTER_TITLE = "Plots projection";

export const PCAScatter = ({
  computedPCA,
  selectedComponents,
  clickedPlots,
  setClickedPlots,
}) => {
  const [scatterOpacity, setScatterOpacity] = useState(0.8);

  const [selectedAttribute, setSelectedAttribute] = useState(null);

  const getPropertyCategory = useGetPropertyCategory();
  const getColor = useGetGroupColor();
  const focusedVarieties = useSelector(
    ({ resultMap }) => resultMap.focusedVarieties
  );
  const [selectedAttributeName, selectedAttributeDate] = selectedAttribute
    ? selectedAttribute.split("@")
    : [];
  const selectedAttributeCategory = useMemo(
    () => getPropertyCategory(selectedAttributeName),
    [getPropertyCategory, selectedAttributeName]
  );
  const getSelectedAttributeColor = useAttributeColor(selectedAttributeName);

  const plotData = useMemo(() => {
    // No grouping to do if attribute is numeric
    let groupedData = {
      [selectedAttribute]: computedPCA.plotValues,
    };
    if (selectedAttributeCategory.attribute)
      groupedData = Object.groupBy(computedPCA.plotValues, ({ features }) => {
        // Return first value within features to not be undefined
        for (const feature of features)
          if (feature.properties[selectedAttributeName] != null)
            return feature.properties[selectedAttributeName];

        return null;
      });

    const traces = Object.entries(groupedData).map(([name, values]) => {
      const x = [];
      const y = [];
      const text = [];
      const colorValues = []; // Used by plotly color scale if data is numeric
      values.forEach(({ features, values }) => {
        x.push(values[selectedComponents[1]]);
        y.push(values[selectedComponents[0]]);
        text.push(features[0].displayId);

        const feature = selectedAttributeDate
          ? features.find(({ date }) => date === selectedAttributeDate)
          : features[0];
        colorValues.push(feature.properties[selectedAttributeName]);
      });

      // If data is not numeric, use attribute colors for non-null else use colorscale
      const markerColor = selectedAttributeCategory.attribute
        ? name !== "null" && name !== "undefined"
          ? getSelectedAttributeColor(name)
          : HIPHEN_GREEN
        : colorValues;

      return {
        x,
        y,
        text,
        mode: "markers",
        type: "scattergl",
        name: name !== "null" ? name : NULL_GROUP_LABEL,
        hovertemplate: "Plot %{text}<br><i>Click to explore</i><extra></extra>",
        textposition: "top",
        marker: {
          color: markerColor,
          colorscale: "Viridis",
          showscale: !selectedAttributeCategory.attribute,
          size: 7.5,
          opacity: scatterOpacity,
        },
      };
    });

    /* Step 1: when not coloring by trait, paint the plots of focused varieties
     * with their focus-list color (same source as the FocusList badges) so the
     * point on the scatter matches its colored badge. Drawn as an overlay trace
     * on top of the base markers. */
    if (selectedAttribute === null && focusedVarieties.length > 0) {
      /* A focus entry is either a variety group (colors all its plots) or a
       * single plot's displayId (colors just that point) — key each plot by
       * whichever is focused so both share the same color source as the badge. */
      const focusedPlots = Object.groupBy(
        computedPCA.plotValues.filter(
          ({ features, displayId }) =>
            focusedVarieties.includes(features[0].group) ||
            focusedVarieties.includes(displayId)
        ),
        ({ features, displayId }) =>
          focusedVarieties.includes(displayId) ? displayId : features[0].group
      );

      Object.entries(focusedPlots).forEach(([group, plots]) => {
        traces.push({
          x: plots.map(({ values }) => values[selectedComponents[1]]),
          y: plots.map(({ values }) => values[selectedComponents[0]]),
          text: plots.map(({ features }) => features[0].displayId),
          mode: "markers",
          type: "scattergl",
          name: "Focused",
          showlegend: false,
          hovertemplate:
            "Plot %{text}<br><i>Click to explore</i><extra></extra>",
          marker: { color: getColor(group), size: 9 },
        });
      });
    }

    /* Highlight the clicked plots with a distinct overlay marker (the same gray
     * as their annotation arrow), drawn on top at full opacity so the selected
     * point reads clearly whatever the opacity slider does to the cloud. */
    if (clickedPlots.length > 0)
      traces.push({
        x: clickedPlots.map((plot) => plot.values[selectedComponents[1]]),
        y: clickedPlots.map((plot) => plot.values[selectedComponents[0]]),
        text: clickedPlots.map((plot) => plot.displayId),
        mode: "markers",
        type: "scattergl",
        name: "Selected",
        showlegend: false,
        hovertemplate: "Plot %{text}<br><i>Click to explore</i><extra></extra>",
        marker: { color: SELECTED_PLOT_COLOR, size: 10, opacity: 1 },
      });

    return traces;
  }, [
    computedPCA,
    selectedAttributeCategory.attribute,
    selectedAttribute,
    scatterOpacity,
    selectedComponents,
    selectedAttributeName,
    selectedAttributeDate,
    focusedVarieties,
    getColor,
    getSelectedAttributeColor,
    clickedPlots,
  ]);

  const layout = useMemo(() => {
    const xIndex = selectedComponents[1];
    const yIndex = selectedComponents[0];

    /* Pin the axes to an explicit data range (+5% padding) instead of letting
     * Plotly autorange. Autorange fits annotations too, so a label arrow landing
     * near an edge would otherwise zoom the whole cloud — that was the graph
     * "jumping" when a selection arrow moved. With a fixed range, adding or
     * moving an arrow can no longer shift the viewport. */
    const range = (index) => {
      let min = Infinity;
      let max = -Infinity;
      computedPCA.plotValues.forEach(({ values }) => {
        if (values[index] < min) min = values[index];
        if (values[index] > max) max = values[index];
      });
      const padding = (max - min) * 0.05 || 1;
      return [min - padding, max + padding];
    };

    // Rank clicked plots by height, then alternate labels above/below by rank
    // so stacked selections spread out instead of all piling on the same side
    const yRankByIndex = clickedPlots
      .map((plot, i) => ({ i, y: plot.values[yIndex] }))
      .sort((a, b) => b.y - a.y)
      .reduce((acc, { i }, rank) => {
        acc[i] = rank;
        return acc;
      }, {});

    return {
      autosize: true,
      showlegend:
        selectedAttribute !== null && selectedAttributeCategory.attribute,
      dragmode: "select",
      margin: {
        r: 20,
        t: 20,
        l: 70,
        b: 80,
      },
      annotations: clickedPlots.map((plot, index) => ({
        x: plot.values[xIndex],
        y: plot.values[yIndex],
        text: `<b>${plot.displayId}</b>`,
        ax: 0,
        ay: yRankByIndex[index] % 2 === 0 ? -38 : 38,
        standoff: 5,
        arrowhead: 2,
        arrowsize: 1,
        arrowwidth: 1.5,
        arrowcolor: SELECTED_PLOT_COLOR,
        bgcolor: "rgba(255, 255, 255, 0.9)",
        bordercolor: "#d4dbc9",
        borderwidth: 1,
        borderpad: 4,
        font: { color: THEME.indicators, size: 12 },
      })),
      xaxis: {
        title: {
          text: computedPCA.components[xIndex].name,
          font: { size: 13 },
        },
        tickfont: { size: 11.5 },
        fixedrange: true,
        autorange: false,
        range: range(xIndex),
      },
      yaxis: {
        title: {
          text: computedPCA.components[yIndex].name,
          font: { size: 13 },
        },
        tickfont: { size: 11.5 },
        fixedrange: true,
        autorange: false,
        range: range(yIndex),
      },
      plot_bgcolor: "transparent",
      paper_bgcolor: "white",

      font: { color: THEME.indicators, size: 13 },
    };
  }, [
    selectedAttribute,
    selectedAttributeCategory.attribute,
    clickedPlots,
    computedPCA.components,
    computedPCA.plotValues,
    selectedComponents,
  ]);

  const config = useMemo(
    () => ({
      ...COMMON_PLOT_CONFIG,
      ...slideExportConfig({
        title: PCA_SCATTER_TITLE,
        filename: `PCAscatter ${selectedAttribute ?? ""}`.trim(),
      }),
    }),
    [selectedAttribute]
  );

  const { trackEvent } = useTracking();

  // A rectangle selection past the threshold waits here for a confirmation.
  const [pendingSelection, setPendingSelection] = useState(null);

  // Adds to the current selection rather than replacing it, so several
  // rectangles build one set. Keyed by id, the same identity handleClick uses to
  // toggle a plot back off.
  const applySelection = useCallback(
    (plots) => {
      setClickedPlots((current) => {
        const merged = new Map(current.map((plot) => [plot.id, plot]));
        plots.forEach((plot) => merged.set(plot.id, plot));
        return [...merged.values()];
      });
    },
    [setClickedPlots]
  );

  // Feeds the same state as a click, so a rectangle selection reaches the plot
  // images and the numbered annotations. It used to land in a local state that
  // only a floating card read, which is why selecting two plots showed nothing
  // in the plot view while the numbers appeared elsewhere (R1-21). Note the
  // shape: `plotValues` entries, not `features[0]`, because that is what the
  // annotations and PlotHistory expect.
  const handleSelection = useCallback(
    (event) => {
      if (!event || event.points.length === 0) return;
      trackEvent(ANALYTICS_EVENTS.SELECT_FROM_PCA_SCATTER);
      const selected = new Map();
      event.points.forEach((point) => {
        const plot = computedPCA.plotValues.find(
          ({ displayId }) => displayId === point.text
        );
        if (plot) selected.set(plot.id, plot);
      });
      const plots = [...selected.values()];
      const total = new Set([
        ...clickedPlots.map(({ id }) => id),
        ...plots.map(({ id }) => id),
      ]).size;
      if (total > PLOTS_TAB_LIMIT) setPendingSelection({ plots, total });
      else applySelection(plots);
    },
    [applySelection, clickedPlots, computedPCA, trackEvent]
  );

  const handleClick = useCallback(
    (event) => {
      if (!event || event.points.length === 0) return;
      trackEvent(ANALYTICS_EVENTS.SELECT_FROM_PCA_SCATTER);
      setClickedPlots((current) => {
        const plot = computedPCA.plotValues.find(
          ({ displayId }) => displayId === event.points[0].text
        );
        if (current.includes(plot))
          return current.filter(({ id }) => id !== plot.id);
        return [...current, plot];
      });
    },
    [computedPCA.plotValues, setClickedPlots, trackEvent]
  );

  const closePending = () => setPendingSelection(null);

  return (
    <>
      <Modal isOpen={pendingSelection !== null} toggle={closePending} centered>
        <ModalHeader toggle={closePending}>Large selection</ModalHeader>
        <ModalBody>
          This selection would bring the Plots tab to{" "}
          <strong>{pendingSelection?.total}</strong> plots, past the{" "}
          {PLOTS_TAB_LIMIT} it holds comfortably. Each plot opens its own card
          with its image history, so going further may slow the page down or
          freeze the tab.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closePending}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              applySelection(pendingSelection.plots);
              closePending();
            }}
          >
            Open {pendingSelection?.total} plots anyway
          </Button>
        </ModalFooter>
      </Modal>
      {computedPCA && (
        <Plot
          data={plotData}
          layout={layout}
          onSelected={handleSelection}
          onClick={handleClick}
          config={config}
          style={PLOT_STYLE}
          useResizeHandler
        />
      )}
      <div className="d-flex gap-2 p-1 align-items-center">
        <i className="fa-solid fa-palette discrete-icon" />
        <PropertySelect
          multiDate
          isClearable
          className="flex-grow-1"
          menuPlacement="top"
          value={selectedAttribute}
          onChange={(value) =>
            value
              ? setSelectedAttribute(value.value)
              : setSelectedAttribute(null)
          }
          placeholder="Color by trait or attribute"
        />
        <i className="fa-solid fa-eye discrete-icon" />
        <div className="flex-grow-1">
          <ReactSlider
            value={scatterOpacity}
            step={0.01}
            min={0}
            max={1}
            onAfterChange={setScatterOpacity}
            className="opacity-slider-track"
            thumbClassName="opacity-slider-thumb"
            withTracks={false}
            renderThumb={(props, state) => {
              const { key, ...rest } = props;
              return (
                <div key={key} {...rest}>
                  {state.valueNow}
                </div>
              );
            }}
          />
        </div>
      </div>
    </>
  );
};

PCAScatter.propTypes = {
  computedPCA: PropTypes.object,
  selectedComponents: PropTypes.arrayOf(PropTypes.number).isRequired,
  clickedPlots: PropTypes.arrayOf(PropTypes.object).isRequired,
  setClickedPlots: PropTypes.func.isRequired,
};
